import React from 'react';
import { Link } from 'react-router-dom';
import { Plane, CheckCircle2, X, ArrowRight } from 'lucide-react';

const Pricing = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1A2B4A] via-[#243552] to-[#1A2B4A]">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-[#1A2B4A]/95 backdrop-blur-sm border-b border-[#4A90E2]/20 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Plane className="h-8 w-8 text-[#4A90E2]" />
              <span className="text-2xl font-heading font-bold text-white">Winglet</span>
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/how-it-works" className="text-gray-300 hover:text-[#4A90E2] transition-colors">How It Works</Link>
              <Link to="/pricing" className="text-[#4A90E2] border-b-2 border-[#4A90E2]">Pricing</Link>
              <Link to="/compare-tools" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Compare</Link>
              <Link to="/about" className="text-gray-300 hover:text-[#4A90E2] transition-colors">About</Link>
              <Link to="/data-privacy" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Data Privacy</Link>
            </div>
            <div className="flex space-x-4">
              <Link to="/login" className="text-gray-300 hover:text-white transition-colors">Sign In</Link>
              <Link to="/signup" className="bg-[#4A90E2] hover:bg-[#3A7BC8] text-white px-6 py-2 rounded-lg transition-colors">
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block mb-4">
            <span className="bg-[#4A90E2]/20 text-[#4A90E2] px-4 py-2 rounded-full text-sm font-semibold">
              🎁 1.5 MONTHS FREE
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-heading font-bold text-white mb-6">
            Simple, Honest <span className="text-[#4A90E2]">Pricing</span>
          </h1>
          <p className="text-xl text-gray-300">
            One plan. All features. Cancel anytime.
          </p>
        </div>
      </section>

      {/* Pricing Card */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-[#4A90E2]/20 to-[#1A2B4A] border-2 border-[#4A90E2] rounded-2xl p-12 text-center">
            <div className="inline-block bg-[#4A90E2] text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
              MOST POPULAR
            </div>
            <h2 className="text-5xl font-heading font-bold text-white mb-2">
              $14.99<span className="text-2xl text-gray-400">/month</span>
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              After your 1.5-month free trial
            </p>
            
            <div className="bg-[#0F1824] border border-[#4A90E2]/30 rounded-xl p-8 mb-8">
              <h3 className="text-2xl font-heading font-bold text-white mb-6">
                Start Free for 45 Days
              </h3>
              <p className="text-gray-300 mb-6">
                Try every feature. Upload rotations. Track flights. Catch pay errors. No commitment.
              </p>
              <Link 
                to="/signup" 
                className="bg-[#4A90E2] hover:bg-[#3A7BC8] text-white px-12 py-4 rounded-lg text-lg font-semibold transition-all transform hover:scale-105 inline-flex items-center group w-full justify-center"
              >
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <div className="grid grid-cols-3 gap-4 mt-6 text-sm">
                <div className="flex items-center justify-center space-x-2">
                  <CheckCircle2 className="h-4 w-4 text-[#4A90E2]" />
                  <span className="text-gray-400">No credit card</span>
                </div>
                <div className="flex items-center justify-center space-x-2">
                  <CheckCircle2 className="h-4 w-4 text-[#4A90E2]" />
                  <span className="text-gray-400">Cancel anytime</span>
                </div>
                <div className="flex items-center justify-center space-x-2">
                  <CheckCircle2 className="h-4 w-4 text-[#4A90E2]" />
                  <span className="text-gray-400">Full access</span>
                </div>
              </div>
            </div>

            <div className="text-left space-y-4">
              <h3 className="text-xl font-heading font-bold text-white mb-4">Everything Included:</h3>
              <FeatureItem text="Automated pay variance detection" />
              <FeatureItem text="PWA Section 23L reroute protection (RED/YELLOW/GREEN alerts)" />
              <FeatureItem text="FAR 117 predictive compliance checking" />
              <FeatureItem text="Real-time flight tracking (OOOI updates)" />
              <FeatureItem text="Timecard audit engine with grievance exports" />
              <FeatureItem text="Rotation credit calculator (DPM, ADG, MCD, 1:2, 1:1.5, 1:3.5)" />
              <FeatureItem text="Calendar sync (Google, Apple, Outlook)" />
              <FeatureItem text="Spillover credit allocation" />
              <FeatureItem text="Desktop web app + mobile responsive" />
              <FeatureItem text="Unlimited rotations and timecards" />
              <FeatureItem text="Email support" />
            </div>
          </div>
        </div>
      </section>

      {/* ROI Calculator */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0F1824]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-heading font-bold text-white text-center mb-6">
            Pays for Itself in <span className="text-[#4A90E2]">One Rotation</span>
          </h2>
          <p className="text-xl text-gray-300 text-center mb-12">
            Average savings vs monthly cost
          </p>
          
          <div className="bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-xl p-8">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">What Winglet Catches:</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Pay errors</span>
                    <span className="text-white font-semibold">✓ Automated detection</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Reroute premiums</span>
                    <span className="text-white font-semibold">✓ PWA Section 23L</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Per diem tracking</span>
                    <span className="text-white font-semibold">✓ Domestic + Intl</span>
                  </div>
                  <div className="border-t border-gray-700 pt-3 flex justify-between">
                    <span className="text-[#4A90E2] font-semibold text-lg">Value:</span>
                    <span className="text-[#4A90E2] font-semibold text-lg">Protection</span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Your Investment:</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Winglet subscription:</span>
                    <span className="text-white font-semibold">$14.99</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Other tools needed:</span>
                    <span className="text-white font-semibold">$0.00</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Time wasted:</span>
                    <span className="text-white font-semibold">$0.00</span>
                  </div>
                  <div className="border-t border-gray-700 pt-3 flex justify-between">
                    <span className="text-red-400 font-semibold text-lg">Total Cost:</span>
                    <span className="text-red-400 font-semibold text-lg">$14.99</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-lg p-6">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-green-400 font-semibold text-xl">Your Monthly Savings:</p>
                  <p className="text-gray-400 text-sm">After Winglet subscription</p>
                </div>
                <p className="text-4xl font-heading font-bold text-white">
                  $285 - $985
                </p>
              </div>
              <div className="mt-4 text-center">
                <p className="text-green-400 font-semibold text-2xl">
                  ROI: 19x - 66x
                </p>
                <p className="text-gray-400 text-sm">Break-even: 1 caught pay error per year</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison to Competitors */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-heading font-bold text-white text-center mb-12">
            Compare to Alternatives
          </h2>
          
          <div className="bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-xl overflow-hidden">
            <div className="grid grid-cols-3 gap-px bg-gray-700">
              {/* Header */}
              <div className="bg-[#1A2B4A] p-6">
                <p className="text-gray-400 text-sm">Solution</p>
              </div>
              <div className="bg-[#0F1824] p-6">
                <p className="text-[#4A90E2] font-semibold">Winglet</p>
                <p className="text-sm text-gray-400">All-in-one</p>
              </div>
              <div className="bg-[#1A2B4A] p-6">
                <p className="text-gray-400">Widget Crew</p>
                <p className="text-sm text-gray-500">Basic pay only</p>
              </div>
              
              {/* Price */}
              <div className="bg-[#1A2B4A] p-6">
                <p className="text-white">Monthly Cost</p>
              </div>
              <div className="bg-[#0F1824] p-6">
                <p className="text-2xl font-bold text-white">$14.99</p>
              </div>
              <div className="bg-[#1A2B4A] p-6">
                <p className="text-2xl font-bold text-white">$9.99</p>
              </div>
              
              {/* Features */}
              <div className="bg-[#1A2B4A] p-6">
                <p className="text-white">Reroute Protection</p>
              </div>
              <div className="bg-[#0F1824] p-6">
                <CheckCircle2 className="h-6 w-6 text-green-400" />
              </div>
              <div className="bg-[#1A2B4A] p-6">
                <X className="h-6 w-6 text-red-400" />
              </div>
              
              <div className="bg-[#1A2B4A] p-6">
                <p className="text-white">Variance Detection</p>
              </div>
              <div className="bg-[#0F1824] p-6">
                <CheckCircle2 className="h-6 w-6 text-green-400" />
              </div>
              <div className="bg-[#1A2B4A] p-6">
                <X className="h-6 w-6 text-red-400" />
              </div>
              
              <div className="bg-[#1A2B4A] p-6">
                <p className="text-white">FAR 117 Predictive</p>
              </div>
              <div className="bg-[#0F1824] p-6">
                <CheckCircle2 className="h-6 w-6 text-green-400" />
              </div>
              <div className="bg-[#1A2B4A] p-6">
                <X className="h-6 w-6 text-red-400" />
              </div>
              
              {/* Value */}
              <div className="bg-[#1A2B4A] p-6">
                <p className="text-white font-semibold">Error Detection</p>
              </div>
              <div className="bg-[#0F1824] p-6">
                <p className="text-[#4A90E2] font-bold">Automatic</p>
              </div>
              <div className="bg-[#1A2B4A] p-6">
                <p className="text-gray-400">Manual checking</p>
              </div>
            </div>
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-gray-300 mb-6">
              <span className="font-semibold text-white">Winglet catches errors others miss.</span><br />
              Automated detection beats manual checking every time.
            </p>
            <Link to="/compare-tools" className="text-[#4A90E2] hover:text-[#3A7BC8] font-semibold inline-flex items-center">
              See Full Feature Comparison
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0F1824]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-heading font-bold text-white text-center mb-12">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <FAQItem 
              question="Do I need a credit card for the free trial?"
              answer="Nope! Start your 1.5-month free trial with just an email address. We'll remind you before the trial ends, and you can choose to subscribe then."
            />
            <FAQItem 
              question="Can I cancel anytime?"
              answer="Yes! Cancel with one click from your settings page. No phone calls, no hassle. Your data stays accessible for 30 days after cancellation."
            />
            <FAQItem 
              question="What happens after my trial ends?"
              answer="We'll send you an email reminder 7 days before your trial ends. If you want to continue, it's $14.99/month. If not, your account will remain free (read-only access to your data)."
            />
            <FAQItem 
              question="Do you offer refunds?"
              answer="Yes! Pro-rated refunds within 30 days of subscription. If you're not happy, we'll refund the unused portion."
            />
            <FAQItem 
              question="Is my rotation data secure?"
              answer="Absolutely. All data encrypted at rest and in transit (AES-256). Hosted on Supabase (AWS infrastructure). SOC 2 compliant. We NEVER sell your data."
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-heading font-bold text-white mb-6">
            Ready to Protect Your Pay?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join hundreds of pilots saving thousands with Winglet.
          </p>
          <Link 
            to="/signup" 
            className="bg-[#4A90E2] hover:bg-[#3A7BC8] text-white px-12 py-5 rounded-lg text-xl font-semibold transition-all transform hover:scale-105 inline-flex items-center group"
          >
            Start Free for 1.5 Months
            <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-1 transition-transform" />
          </Link>
          <p className="text-gray-400 mt-4">
            No credit card · No commitment · Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F1824] border-t border-gray-800 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Branding */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Plane className="h-6 w-6 text-[#4A90E2]" />
                <span className="text-xl font-heading font-bold text-white">Winglet</span>
              </div>
              <p className="text-gray-400 text-sm">The AI-powered co-pilot for your career.</p>
            </div>
            
            {/* Product Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/how-it-works" className="text-gray-400 hover:text-[#4A90E2] transition-colors">How It Works</Link></li>
                <li><Link to="/pricing" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Pricing</Link></li>
                <li><Link to="/compare-tools" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Compare</Link></li>
              </ul>
            </div>
            
            {/* Company Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="text-gray-400 hover:text-[#4A90E2] transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Contact</Link></li>
                <li><Link to="/data-privacy" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Data Privacy</Link></li>
              </ul>
            </div>
            
            {/* Legal Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          {/* Copyright */}
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>© 2026 Winglet. Built by pilots, for pilots. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const FeatureItem = ({ text }) => (
  <div className="flex items-start space-x-3">
    <CheckCircle2 className="h-6 w-6 text-[#4A90E2] flex-shrink-0 mt-0.5" />
    <span className="text-gray-300">{text}</span>
  </div>
);

const FAQItem = ({ question, answer }) => (
  <div className="bg-[#1A2B4A] border border-[#4A90E2]/20 rounded-xl p-6">
    <h3 className="text-lg font-heading font-bold text-white mb-2">{question}</h3>
    <p className="text-gray-300">{answer}</p>
  </div>
);

export default Pricing;
